import React from 'react';
import PropTypes from 'prop-types';
import {useHistory} from 'react-router-dom';
import {ButtonInfo} from 'components/buttons';
import {
  MainPageCardButton,
  MainPageContainerText,
  MainPageContainerTitle
} from 'routes/App/MainPage/styles';
import JoinCashback from 'assets/mainPage/join-cashback-img.svg';
import IconCashbackCycle from 'assets/mainPage/growthPrograms/icon-cashback-cycle.svg';
import IconCashbackEarned from 'assets/mainPage/growthPrograms/icon-cashback-earned.svg';
import IconCashbackRedeemed from 'assets/mainPage/growthPrograms/icon-cashback-redeemed.svg';
import {
  GrowthPromoBadge,
  GrowthPromoBody,
  GrowthPromoCard,
  GrowthPromoHeader,
  GrowthPromoLeft,
  GrowthPromoRight,
  GrowthWidgetCard,
  GrowthWidgetColumn,
  GrowthWidgetFooter,
  GrowthWidgetHeader,
  GrowthWidgetHint,
  GrowthWidgetInfo,
  GrowthWidgetMetric,
  GrowthWidgetSplit
} from 'routes/App/MainPage/Components/GrowthPrograms/styles';
import {
  formatCurrency,
  formatTrendPercent,
  getCashbackWidgetState
} from 'routes/App/MainPage/Components/GrowthPrograms/utils';

const LEARN_MORE_URL = 'https://support.getsauce.com/cashback-how-it-works';
const GET_STARTED_URL =
  'https://share.hsforms.com/1ltpnwBKiRbSSLMibffSkQg21htm';

const CashbackWidget = ({
  isEnrolled,
  showLimitedTimeOffer,
  redeemedOrdersTotal,
  ordersEarned,
  ordersEarnedTrend,
  ordersRedeemed,
  ordersRedeemedTrend
}) => {
  const history = useHistory();
  const state = getCashbackWidgetState({
    isEnrolled,
    redeemedOrdersTotal
  });
  const earnedTrend = formatTrendPercent(ordersEarnedTrend);
  const redeemedTrend = formatTrendPercent(ordersRedeemedTrend);

  const goToCashback = () => {
    history.push('/cashback');
  };

  if (state === 'notEnrolled') {
    return (
      <GrowthPromoCard>
        <GrowthPromoHeader>
          <MainPageContainerTitle>Cashback Program</MainPageContainerTitle>
          <GrowthPromoBadge>
            {showLimitedTimeOffer ? 'FREE FOR LIMITED TIME' : 'New'}
          </GrowthPromoBadge>
        </GrowthPromoHeader>
        <GrowthPromoBody>
          <GrowthPromoLeft>
            <div>
              <MainPageContainerText className='font12 mTop6 withImg'>
                Turn first-time orders into regulars — customers earn credits on
                every order and come back to use them.
              </MainPageContainerText>
            </div>
            <MainPageCardButton className='buttonsGroup'>
              <span onClick={() => window.open(GET_STARTED_URL, '_blank')}>
                Get started
              </span>
              <span onClick={() => window.open(LEARN_MORE_URL, '_blank')}>
                Learn more
              </span>
            </MainPageCardButton>
          </GrowthPromoLeft>
          <GrowthPromoRight>
            <img src={JoinCashback} alt='Cashback' />
          </GrowthPromoRight>
        </GrowthPromoBody>
      </GrowthPromoCard>
    );
  }

  if (state === 'early') {
    return (
      <GrowthWidgetCard>
        <GrowthWidgetHeader className='withInfoPadding'>
          <img className='widgetIcon' src={IconCashbackCycle} alt='' />
          <span className='title'>Cashback</span>
        </GrowthWidgetHeader>
        <GrowthWidgetInfo>
          <ButtonInfo className='OutlineBig ButtonInfoDrop'>
            <div className='DropInfo DropInfoMainPage LeftSide'>
              Orders where customers earned Cashback. Trend compares to last
              week.
            </div>
          </ButtonInfo>
        </GrowthWidgetInfo>
        <GrowthWidgetMetric>
          {formatCurrency(ordersEarned)}
          {earnedTrend && (
            <span className={`trend${earnedTrend.className}`}>
              {earnedTrend.label}
            </span>
          )}
        </GrowthWidgetMetric>
        <GrowthWidgetHint>
          Credits are building — redemptions will appear here soon
        </GrowthWidgetHint>
        <GrowthWidgetFooter>
          <span onClick={goToCashback}>View tips to boost</span>
        </GrowthWidgetFooter>
      </GrowthWidgetCard>
    );
  }

  return (
    <GrowthWidgetCard>
      <GrowthWidgetInfo>
        <ButtonInfo className='OutlineBig ButtonInfoDrop'>
          <div className='DropInfo DropInfoMainPage LeftSide'>
            Orders where customers earned or redeemed Cashback. Trend compares
            to last week.
          </div>
        </ButtonInfo>
      </GrowthWidgetInfo>
      <GrowthWidgetSplit className='withInfo'>
        <GrowthWidgetColumn>
          <GrowthWidgetHeader>
            <img className='widgetIcon' src={IconCashbackEarned} alt='' />
            <span className='title'>Cashback Earned</span>
          </GrowthWidgetHeader>
          <GrowthWidgetMetric>
            {formatCurrency(ordersEarned)}
            {earnedTrend && (
              <span className={`trend${earnedTrend.className}`}>
                {earnedTrend.label}
              </span>
            )}
          </GrowthWidgetMetric>
        </GrowthWidgetColumn>
        <GrowthWidgetColumn>
          <GrowthWidgetHeader>
            <img className='widgetIcon' src={IconCashbackRedeemed} alt='' />
            <span className='title'>Cashback Redeemed</span>
          </GrowthWidgetHeader>
          <GrowthWidgetMetric>
            {formatCurrency(ordersRedeemed)}
            {redeemedTrend && (
              <span className={`trend${redeemedTrend.className}`}>
                {redeemedTrend.label}
              </span>
            )}
          </GrowthWidgetMetric>
        </GrowthWidgetColumn>
      </GrowthWidgetSplit>
      <GrowthWidgetFooter>
        <span onClick={goToCashback}>View details</span>
      </GrowthWidgetFooter>
    </GrowthWidgetCard>
  );
};

CashbackWidget.propTypes = {
  isEnrolled: PropTypes.bool,
  showLimitedTimeOffer: PropTypes.bool,
  redeemedOrdersTotal: PropTypes.number,
  ordersEarned: PropTypes.number,
  ordersEarnedTrend: PropTypes.number,
  ordersRedeemed: PropTypes.number,
  ordersRedeemedTrend: PropTypes.number
};

CashbackWidget.defaultProps = {
  isEnrolled: false,
  showLimitedTimeOffer: false,
  redeemedOrdersTotal: 0,
  ordersEarned: 0,
  ordersEarnedTrend: null,
  ordersRedeemed: 0,
  ordersRedeemedTrend: null
};

export default CashbackWidget;
